/**
 * ATEEZ BADDIE — Shopify-compatible script.js
 * 
 * Reads ALL asset URLs from window.ATEEZ_ASSETS (injected by Liquid template).
 * Falls back to relative paths for local dev if config is missing.
 * 
 * PATCHED 2026-06-18:
 *   #2 — Removed duplicate RAF caption sync (startCaptionSync/stopCaptionSync).
 *         ws.on('timeupdate') is now the single source of truth for caption scroll.
 *   #3 — Loading screen 4000ms → 2000ms.
 *   #4 — Overlay-not-loaded polling replaced with Promise-based ensureOverlayLoaded.
 *         captureFromVideo is now async. If overlay never loads, photo saves without
 *         filter + console.warn (instead of silent 5s setInterval timeout).
 *   #6 — START_DATE now UTC ('Z' suffix) so unlock boundary doesn't shift by TZ.
 *   #7 — doScatter() triple-call in collage forEach → single destructure (3 reflows → 1).
 *   NOT PATCHED (HTML-side, needs Liquid template edit):
 *   #5 — Filter buttons need aria-label="No filter" / "Popup filter" / "Standard 1 filter".
 *   INTENTIONALLY KEPT:
 *   #1 — MOCK_TODAY stays live while beta testing. Remove before launch.
 */
document.addEventListener('DOMContentLoaded', () => {
    // ─── ASSET CONFIG (injected by Liquid or use fallbacks for local dev) ───
    const A = window.ATEEZ_ASSETS || {
        cursor: 'assets/BAD_cursor_32.png',
        icons: {},
        voiceImages: {},
        voiceCaptions: {},
        voiceAudio: {},
        filters: {},
        photos: {},
        photosBase: 'assets/photos/'
    };

    // Helper: get asset URL with fallback
    function iconUrl(name)  { return (A.icons && A.icons[name]) || `assets/voice-notes/icons/${name}.svg`; }
    function voiceImgUrl(n) { return (A.voiceImages && A.voiceImages[n]) || `assets/voice-notes/images/day${n}.png`; }
    function captionUrl(n)  { return (A.voiceCaptions && A.voiceCaptions[n]) || `assets/voice-notes/captions/day${n}.json`; }
    function audioUrl(n)    { return (A.voiceAudio && A.voiceAudio[n]) || `assets/voice-notes/day${n}.mp3`; }
    function filterUrl(key) { return (A.filters && A.filters[key]) || ''; }
    function photoUrl(name) {
        if (A.photos && A.photos[name]) return A.photos[name];
        return (A.photosBase || 'assets/photos/') + name;
    }

    const cursorImgUrl = A.cursor || 'assets/BAD_cursor_32.png';

    // --- Fake Initial Loading Screen ---
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
        setTimeout(() => {
            loadingScreen.classList.add('slide-out');
            setTimeout(() => {
                const p3 = document.getElementById('page-3');
                if (p3) p3.classList.add('swipe-in');
            }, 200);
            setTimeout(() => {
                const p2 = document.getElementById('page-2');
                if (p2) p2.classList.add('swipe-in');
            }, 450);
            setTimeout(() => {
                const p1 = document.getElementById('page-1');
                if (p1) p1.classList.add('swipe-in');
            }, 700);
            setTimeout(() => {
                loadingScreen.remove();
                document.querySelector('.column-container')?.removeAttribute('aria-hidden');
                document.body.classList.remove('overflow-hidden');
            }, 800);
        }, 2000);  // [#3] was 4000
    }

    // --- Trailing cursor (desktop only) ---
    const isTouch = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!isTouch) {
        const trail = document.createElement('img');
        trail.src = cursorImgUrl;
        trail.className = 'cursor-trail';
        trail.alt = '';
        document.body.appendChild(trail);

        let mouseX = window.innerWidth / 2;
        let mouseY = window.innerHeight / 2;
        let trailX = mouseX;
        let trailY = mouseY;
        const ease = 0.12;
        const deadZone = 0.5;

        window.addEventListener('mousemove', (e) => {
            mouseX = e.clientX;
            mouseY = e.clientY;
        }, { passive: true });

        function tick() {
            const dx = mouseX - trailX;
            const dy = mouseY - trailY;
            if (Math.abs(dx) > deadZone || Math.abs(dy) > deadZone) {
                trailX += dx * ease;
                trailY += dy * ease;
                trail.style.left = trailX + 'px';
                trail.style.top = trailY + 'px';
            }
            requestAnimationFrame(tick);
        }
        trail.style.left = trailX + 'px';
        trail.style.top = trailY + 'px';
        tick();
    }

    // --- Column Navigation Logic ---
    const columns = document.querySelectorAll('.column-page');
    columns.forEach(col => {
        col.addEventListener('click', (e) => {
            if (col.classList.contains('active') && e.target.closest('.day-item, input, button, a, .photo-strip-thumb, .photo-window, .de-file, .audio-window')) {
                return;
            }
            columns.forEach(c => {
                c.classList.remove('active');
            });
            col.classList.add('active');

            if (col.id === 'page-2') {
                    initCamera();
                } else if (col.id === 'page-3') {
                initCollage();
            } else {
                stopCamera();
            }
        });
    });

    // ============================================
    // PAGE 1: DESKTOP VOICE NOTE EXPERIENCE
    // ============================================
    const START_DATE = new Date('2026-06-21T00:00:00');

    const CURRENT_DATE = new Date();

    const DE_FILES = [
        { id: 1, icon: 'ship',    label: 'Voice Note 1', dayNum: '21' },
        { id: 2, icon: 'compass', label: 'Voice Note 2', dayNum: '22' },
        { id: 3, icon: 'anchor',  label: 'Voice Note 3', dayNum: '23' },
        { id: 4, icon: 'star',    label: 'Voice Note 4', dayNum: '24' },
        { id: 5, icon: 'skull',   label: 'Voice Note 5', dayNum: '25' },
        { id: 6, icon: 'crown',   label: 'Voice Note 6', dayNum: '26' },
        { id: 7, icon: 'rose',    label: 'Voice Note 7', dayNum: '27' },
        { id: 8, icon: 'flame',   label: 'Voice Note 8', dayNum: '28' },
    ];
    const DE_TOTAL = DE_FILES.length;
    const DE_LAYOUT_KEY = 'ateez_de_layout_v3';

    function isUnlocked(dayIndex) {
        const releaseDate = new Date(START_DATE);
        releaseDate.setDate(releaseDate.getDate() + dayIndex);
        const today = new Date(CURRENT_DATE);
        today.setHours(0, 0, 0, 0);
        releaseDate.setHours(0, 0, 0, 0);
        return today >= releaseDate;
    }

    function deGetLayout() {
        let layout = localStorage.getItem(DE_LAYOUT_KEY);
        if (!layout) {
            const gridPoints = [
                { x: 12, y: 20 }, { x: 36, y: 20 }, { x: 60, y: 20 }, { x: 84, y: 20 },
                { x: 12, y: 60 }, { x: 36, y: 60 }, { x: 60, y: 60 }, { x: 84, y: 60 }
            ];
            for (let i = gridPoints.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [gridPoints[i], gridPoints[j]] = [gridPoints[j], gridPoints[i]];
            }
            const positions = [];
            for (let i = 0; i < DE_TOTAL; i++) {
                const base = gridPoints[i];
                const x = base.x + (Math.random() - 0.5) * 8;
                const y = base.y + (Math.random() - 0.5) * 8;
                const rot = (Math.random() - 0.5) * 24;
                positions.push({ x, y, rot });
            }
            layout = JSON.stringify(positions);
            localStorage.setItem(DE_LAYOUT_KEY, layout);
        } else {
            layout = JSON.parse(layout);
        }
        return typeof layout === 'string' ? JSON.parse(layout) : layout;
    }

    const page1 = document.getElementById('page-1');
    const env = document.getElementById('desktop-env');
    let deSelectedId = null;

    function renderDesktop() {
        if (!env) return;
        env.innerHTML = '';
        const layout = deGetLayout();
        const safe = document.createElement('div');
        safe.className = 'de-safe';
        env.appendChild(safe);

        DE_FILES.forEach((f, i) => {
            const pos = layout[i] || { x: 50, y: 50, rot: 0 };
            const unlocked = isUnlocked(i);
            const rot = pos.rot || 0;

            const el = document.createElement('div');
            el.className = `de-file ${unlocked ? 'unlocked' : 'locked'}`;
            el.style.left = pos.x + '%';
            el.style.top = pos.y + '%';
            el.style.transform = `translate(-50%, -50%) scale(0.6) rotate(${rot}deg)`;
            el.dataset.id = f.id;

            if (unlocked) {
                el.setAttribute('role', 'button');
                el.setAttribute('aria-label', f.label);
                el.style.cursor = 'pointer';

                const iconImg = document.createElement('img');
                iconImg.className = 'de-file-icon';
                iconImg.src = iconUrl(f.icon);  // <-- CONFIG
                iconImg.alt = '';
                el.appendChild(iconImg);

                const label = document.createElement('div');
                label.className = 'de-file-label';
                label.textContent = f.label;
                el.appendChild(label);

                let lastTapTime = 0;
                el.addEventListener('pointerup', (e) => {
                    if (!page1.classList.contains('active')) return;
                    e.stopPropagation();
                    selectFile(f.id);
                    if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                        openFile(f.id);
                    } else {
                        const now = Date.now();
                        if (now - lastTapTime < 450) {
                            openFile(f.id);
                            lastTapTime = 0;
                        } else {
                            lastTapTime = now;
                        }
                    }
                });
                el.addEventListener('dblclick', (e) => {
                    if (!page1.classList.contains('active')) return;
                    e.stopPropagation();
                    openFile(f.id);
                });
            } else {
                el.style.cursor = 'default';
                const calIcon = document.createElement('div');
                calIcon.className = 'de-calendar-icon';
                calIcon.innerHTML = `
                    <div class="de-cal-header"></div>
                    <div class="de-cal-day">${f.dayNum}</div>
                `;
                el.appendChild(calIcon);

                const label = document.createElement('div');
                label.className = 'de-file-label locked-label';
                label.textContent = f.label;
                el.appendChild(label);
            }

            setTimeout(() => {
                el.classList.add('visible');
                el.style.transform = `translate(-50%, -50%) scale(1) rotate(${rot}deg)`;
            }, 180 + i * 70);

            safe.appendChild(el);
        });
    }

    function selectFile(id) {
        deSelectedId = id;
        env.querySelectorAll('.de-file').forEach(el => {
            el.classList.toggle('selected', parseInt(el.dataset.id) === id);
        });
    }

    // ----- AUDIO WINDOW -----
    const awWindow = document.getElementById('audio-window');
    const awTitle = document.getElementById('aw-title');
    const awClose = document.getElementById('aw-close');
    const awImage = document.getElementById('aw-image');
    const awCaptionScroll = document.getElementById('aw-caption-scroll');
    const awPlay = document.getElementById('aw-play');
    const awVolume = document.getElementById('aw-volume');

    let ws = null;
    let currentCaptionData = null;
    // [#2] captionRafId removed — RAF loop deleted, timeupdate is single source
    let activeOpenFileId = null;

    function openFile(id) {
        const file = DE_FILES.find(f => f.id === id);
        if (!file) return;
        activeOpenFileId = id;

        awTitle.textContent = file.label;
        awImage.src = voiceImgUrl(id);  // <-- CONFIG
        awCaptionScroll.innerHTML = '<div style="color:#666;">Loading transcript...</div>';

        awWindow.style.display = '';
        void awWindow.offsetWidth;
        awWindow.classList.add('open');

        // Load caption JSON
        const cUrl = captionUrl(id);  // <-- CONFIG
        fetch(cUrl).then(r => r.ok ? r.json() : Promise.reject('No caption'))
            .then(data => {
                currentCaptionData = data;
                awTitle.textContent = data.title || file.label;
                awCaptionScroll.innerHTML = '';
                (data.transcript || []).forEach((line, idx) => {
                    const p = document.createElement('span');
                    p.className = 'aw-caption-line';
                    p.dataset.idx = idx;
                    p.textContent = line;
                    awCaptionScroll.appendChild(p);
                });
                // [#2] startCaptionSync() call removed — ws.on('timeupdate') handles caption sync
            })
            .catch(() => {
                currentCaptionData = null;
                awCaptionScroll.innerHTML = '<div style="color:#666;">(No transcript available)</div>';
            });

        if (ws) {
            ws.destroy();
            ws = null;
        }

        const aUrl = audioUrl(id);  // <-- CONFIG
        ws = WaveSurfer.create({
            container: '#aw-waveform',
            waveColor: 'rgba(255,255,255,0.35)',
            progressColor: '#FE7CD7',
            cursorColor: 'transparent',
            height: 40,
            barWidth: 2,
            barRadius: 2,
            barGap: 2,
            normalize: true,
            interact: true,
            dragToSeek: false,
            url: aUrl,
        });
        ws.on('play', () => {
            awPlay.textContent = '❚❚';
            awPlay.classList.add('playing');
        });
        ws.on('pause', () => {
            awPlay.textContent = '▶';
            awPlay.classList.remove('playing');
        });
        ws.on('finish', () => {
            awPlay.textContent = '▶';
            awPlay.classList.remove('playing');
            // [#2] stopCaptionSync() removed — no RAF loop to stop
        });
        // [#2] Single source of truth for caption sync: timeupdate event.
        // No competing RAF loop. Event-driven, no wasted frames when paused.
        ws.on('timeupdate', (currentTime) => {
            if (!currentCaptionData || !ws.isPlaying()) return;
            const dur = ws.getDuration() || 1;
            const lines = awCaptionScroll.querySelectorAll('.aw-caption-line');
            if (lines.length === 0) return;
            const activeIdx = Math.min(lines.length - 1, Math.floor((currentTime / dur) * lines.length));
            lines.forEach((l, i) => {
                if (i === activeIdx) { l.classList.add('active'); l.classList.remove('past'); }
                else if (i < activeIdx) { l.classList.remove('active'); l.classList.add('past'); }
                else { l.classList.remove('active'); l.classList.remove('past'); }
            });
            const active = lines[activeIdx];
            if (active) {
                const scrollH = awCaptionScroll.clientHeight;
                const lineTop = active.offsetTop;
                const target = lineTop - scrollH / 2 + active.offsetHeight / 2;
                awCaptionScroll.scrollTop = target;
            }
        });
        ws.on('ready', () => {
            ws.setVolume(parseFloat(awVolume.value));
            ws.play();
        });
    }

    function closeAudioWindow() {
        if (ws) { ws.pause(); ws.destroy(); ws = null; awPlay.textContent = '▶'; awPlay.classList.remove('playing'); }
        awWindow.classList.remove('open');
        setTimeout(() => {
            awWindow.style.display = 'none';
            activeOpenFileId = null;
            // [#2] stopCaptionSync() removed
        }, 280);
    }

    awClose.addEventListener('click', (e) => {
        e.stopPropagation();
        closeAudioWindow();
    });

    awPlay.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!ws) return;
        if (ws.isPlaying()) ws.pause();
        else ws.play();
    });

    awVolume.addEventListener('input', (e) => { e.stopPropagation(); if (ws) ws.setVolume(parseFloat(awVolume.value)); });

    awWindow.addEventListener('click', (e) => e.stopPropagation());
    awWindow.addEventListener('dblclick', (e) => e.stopPropagation());

    // [#2] DELETED: startCaptionSync() and stopCaptionSync() functions (~70 lines).
    //       The RAF loop was a duplicate of the ws.on('timeupdate') handler above.
    //       Both did identical Math.floor(t/dur*lines.length) + scroll math.
    //       timeupdate is event-driven (fires only when audio plays), RAF ran every
    //       frame regardless — wasteful and caused potential scroll jitter from
    //       competing scrollTop writes. timeupdate is now the only caption sync.

    renderDesktop();

    const dePageObs = new MutationObserver(() => {
        if (!page1.classList.contains('active')) {
            closeAudioWindow();
            deSelectedId = null;
            env.querySelectorAll('.de-file.selected').forEach(el => el.classList.remove('selected'));
        }
    });
    dePageObs.observe(page1, { attributes: true, attributeFilter: ['class'] });

    // ============================================
    // PAGE 2: PHOTO BOOTH LOGIC
    // ============================================
    const MAX_PHOTOS = 9;
    const video = document.getElementById('webcam-video');
    const preview = document.getElementById('camera-preview');
    const canvas = document.getElementById('capture-canvas');
    const cameraInput = document.getElementById('native-camera-input');
    const btnCapture = document.getElementById('btn-capture');
    const btnEffects = document.getElementById('btn-effects');
    const filmstrip = document.querySelector('.pb-k-filmstrip');
    const previewContainer = document.querySelector('.pb-k-camera');

    let captureMode = 'webcam';
    let photos = [];
    let photoReady = false;
    const shutterCaptureSVG = '<svg class="w-6 h-6 md:w-7 md:h-7" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke="white" stroke-width="2"/><circle cx="12" cy="12" r="5" fill="white"/></svg>';
    const shutterDownloadSVG = '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><polyline points="7 10 12 15 17 10" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="12" y1="15" x2="12" y2="3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    function setShutterMode(mode) {
        btnCapture.innerHTML = mode === 'download' ? shutterDownloadSVG : shutterCaptureSVG;
        btnCapture.setAttribute('aria-label', mode === 'download' ? 'Download photo' : 'Take photo');
    }

    function renderEmptyFilmstrip() {
        filmstrip.innerHTML = '';
        for (let i = 0; i < MAX_PHOTOS; i++) {
            const thumb = document.createElement('div');
            thumb.className = 'photo-strip-thumb empty';
            filmstrip.appendChild(thumb);
        }
    }

    function updateFilmstrip() {
        filmstrip.innerHTML = '';
        const reversed = [...photos].reverse();
        reversed.forEach((photo, idx) => {
            const thumb = document.createElement('div');
            thumb.className = 'photo-strip-thumb';
            const img = document.createElement('img');
            img.src = photo.dataUrl;
            img.draggable = false;
            thumb.appendChild(img);
            thumb.addEventListener('click', (e) => {
                e.stopPropagation();
                filmstrip.querySelectorAll('.photo-strip-thumb').forEach(t => t.classList.remove('selected'));
                thumb.classList.add('selected');
                showPhotoInPreview(photo.dataUrl);
            });
            filmstrip.appendChild(thumb);
        });
        for (let i = photos.length; i < MAX_PHOTOS; i++) {
            const thumb = document.createElement('div');
            thumb.className = 'photo-strip-thumb empty';
            filmstrip.appendChild(thumb);
        }
    }

    function triggerFlash() {
        const flash = previewContainer.querySelector('.pb-k-flash');
        if (flash) {
            flash.style.opacity = '0.9';
            setTimeout(() => { flash.style.opacity = '0'; }, 300);
        }
        btnCapture.classList.add('flash');
        setTimeout(() => {
            btnCapture.classList.remove('flash');
        }, 400);
    }

    function showPhotoInPreview(dataUrl) {
        video.style.display = 'none';
        preview.src = dataUrl;
        preview.style.display = 'block';
        if (liveOverlay) liveOverlay.style.display = 'none';
        window.activePreviewDataUrl = dataUrl;
        setShutterMode('download');
        photoReady = true;
    }

    function showLivePreview() {
        preview.style.display = 'none';
        if (window.videoStream) {
            video.style.display = 'block';
        }
        if (liveOverlay && currentFilter !== 'normal' && liveOverlay.src) {
            liveOverlay.style.display = '';
        }
        window.activePreviewDataUrl = null;
        setShutterMode('capture');
    }

    function setupMobileCapture() {
        captureMode = 'file';
        photoReady = false;
        setShutterMode('capture');
        video.style.display = 'none';
        renderEmptyFilmstrip();
    }

    function initCamera() {
        captureMode = 'webcam';
        photoReady = false;
        setShutterMode('capture');
        navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 800 }, height: { ideal: 600 }, facingMode: 'user' } })
            .then(stream => {
                window.videoStream = stream;
                video.srcObject = stream;
                video.style.display = 'block';
                preview.style.display = 'none';
                renderEmptyFilmstrip();
            })
            .catch(err => {
                console.warn('Webcam denied/unavailable, falling back to file upload:', err);
                setupMobileCapture();
            });
    }

    function stopCamera() {
        if (window.videoStream) {
            window.videoStream.getTracks().forEach(track => track.stop());
            window.videoStream = null;
        }
    }

    // ============================================
    // FILTER SYSTEM
    // ============================================
    const POPUP_OVERLAY_PATH = filterUrl('popup');    // <-- CONFIG
    const STANDARD1_OVERLAY_PATH = filterUrl('standard1');  // <-- CONFIG

    let currentFilter = 'normal';
    let popupOverlayImg = null;
    let popupOverlayLoading = false;
    let standard1OverlayImg = null;
    let standard1OverlayLoading = false;

    function loadPopupOverlay() {
        if (popupOverlayImg || popupOverlayLoading || !POPUP_OVERLAY_PATH) return popupOverlayImg;
        popupOverlayLoading = true;
        const img = new Image();
        img.onload = () => { popupOverlayImg = img; console.log('Popup overlay loaded'); };
        img.onerror = () => { console.warn('Popup overlay missing:', POPUP_OVERLAY_PATH); popupOverlayLoading = false; };
        img.src = POPUP_OVERLAY_PATH;
        return null;
    }

    function loadStandard1Overlay() {
        if (standard1OverlayImg || standard1OverlayLoading || !STANDARD1_OVERLAY_PATH) return standard1OverlayImg;
        standard1OverlayLoading = true;
        const img = new Image();
        img.onload = () => { standard1OverlayImg = img; console.log('Standard1 overlay loaded'); };
        img.onerror = () => { console.warn('Standard1 overlay missing:', STANDARD1_OVERLAY_PATH); standard1OverlayLoading = false; };
        img.src = STANDARD1_OVERLAY_PATH;
        return null;
    }

    // [#4] Promise-based overlay loading — replaces setInterval polling in captureFromVideo.
    // Caches the Promise so repeated calls don't re-fetch. Resolves to Image or null.
    const overlayPromises = {};
    function ensureOverlayLoaded(key) {
        const cached = (key === 'popup') ? popupOverlayImg : (key === 'standard1') ? standard1OverlayImg : null;
        if (cached) return Promise.resolve(cached);
        if (overlayPromises[key]) return overlayPromises[key];
        const src = (key === 'popup') ? POPUP_OVERLAY_PATH : (key === 'standard1') ? STANDARD1_OVERLAY_PATH : null;
        if (!src) return Promise.resolve(null);
        overlayPromises[key] = new Promise(resolve => {
            const probe = new Image();
            probe.onload = () => {
                if (key === 'popup') popupOverlayImg = probe;
                else standard1OverlayImg = probe;
                resolve(probe);
            };
            probe.onerror = () => resolve(null);
            probe.src = src;
        });
        return overlayPromises[key];
    }

    const filterBtns = document.querySelectorAll('.pb-k-circle');
    const liveOverlay = document.getElementById('live-filter-overlay');
    const cameraContainer = document.querySelector('.pb-k-camera');
    const photoboothWindow = document.querySelector('.pb-k-window');

    function setLiveOverlaySrc(src) {
        if (!liveOverlay) return;
        if (!src) {
            liveOverlay.style.display = 'none';
            liveOverlay.removeAttribute('src');
            if (photoboothWindow) {
                photoboothWindow.style.removeProperty('aspect-ratio');
            }
        } else {
            const probe = new Image();
            probe.onload = () => {
                if (photoboothWindow && probe.naturalWidth && probe.naturalHeight) {
                    photoboothWindow.style.aspectRatio = `${probe.naturalWidth} / ${probe.naturalHeight}`;
                }
            };
            probe.src = src;
            liveOverlay.src = src;
            liveOverlay.style.display = '';
        }
    }
    if (liveOverlay) {
        liveOverlay.addEventListener('load', () => {
            if (liveOverlay.src) liveOverlay.style.display = '';
        });
        liveOverlay.addEventListener('error', () => {
            liveOverlay.style.display = 'none';
        });
    }

    filterBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            filterBtns.forEach(b => {
                b.style.background = 'linear-gradient(180deg,#ddd,#aaa)';
                b.style.color = '#444';
            });
            btn.style.background = 'linear-gradient(180deg,#ffe066,#d4a800)';
            btn.style.color = '#222';
            currentFilter = btn.dataset.filter;

            if (currentFilter === 'popup') {
                loadPopupOverlay();
                setLiveOverlaySrc(POPUP_OVERLAY_PATH);
            } else if (currentFilter === 'standard1') {
                loadStandard1Overlay();
                setLiveOverlaySrc(STANDARD1_OVERLAY_PATH);
            } else {
                setLiveOverlaySrc(null);
            }
        });
    });

    btnCapture.addEventListener('click', (e) => {
        e.stopPropagation();
        if (photoReady) {
            downloadLastPhoto();
            return;
        }
        if (window.activePreviewDataUrl) {
            downloadLastPhoto();
            return;
        }
        if (captureMode === 'file') {
            cameraInput.click();
        } else {
            captureFromVideo();
        }
    });

    if (btnEffects) {
        btnEffects.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }

    cameraInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = async () => {
                const maxWidth = 800;
                const scale = Math.min(1, maxWidth / img.width);
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                // [#4] file-upload path also uses Promise-based overlay
                if (currentFilter === 'popup' || currentFilter === 'standard1') {
                    const overlay = await ensureOverlayLoaded(currentFilter);
                    if (overlay) drawOverlayCover(ctx, overlay, canvas.width, canvas.height);
                    else console.warn(`[file-upload] ${currentFilter} overlay not loaded — saving without filter`);
                }
                savePhoto();
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
        cameraInput.value = '';
    });

    // [#4] Now async. Overlay loading via Promise instead of setInterval polling.
    // If overlay never loads, photo saves WITHOUT filter + console.warn (visible)
    // instead of silently saving unfiltered after 5s timeout.
    async function captureFromVideo() {
        console.log('[captureFromVideo] videoStream:', !!window.videoStream, 'filter:', currentFilter);
        if (!window.videoStream) return;

        const vw = video.videoWidth || 800;
        const vh = video.videoHeight || 600;

        // [#4] If filter active, await overlay before sizing canvas (overlay sets aspect).
        let overlayForFilter = null;
        let targetAspect = null;
        if (currentFilter === 'popup' || currentFilter === 'standard1') {
            overlayForFilter = await ensureOverlayLoaded(currentFilter);
            if (overlayForFilter && overlayForFilter.naturalWidth && overlayForFilter.naturalHeight) {
                targetAspect = overlayForFilter.naturalWidth / overlayForFilter.naturalHeight;
            }
        }

        const containerW = cameraContainer ? cameraContainer.clientWidth : (video.clientWidth || vw);
        const containerH = cameraContainer ? cameraContainer.clientHeight : (video.clientHeight || vh);

        let dw, dh;
        if (targetAspect) {
            if (containerW / containerH > targetAspect) {
                dh = containerH;
                dw = Math.round(containerH * targetAspect);
            } else {
                dw = containerW;
                dh = Math.round(containerW / targetAspect);
            }
        } else {
            dw = containerW;
            dh = containerH;
        }

        canvas.width = dw;
        canvas.height = dh;
        const ctx = canvas.getContext('2d');

        triggerFlash();

        const scale = Math.min(dw / vw, dh / vh);
        const renderedW = vw * scale;
        const renderedH = vh * scale;
        const offsetX = (dw - renderedW) / 2;
        const offsetY = (dh - renderedH) / 2;

        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, dw, dh);
        ctx.drawImage(video, 0, 0, vw, vh, offsetX, offsetY, renderedW, renderedH);

        // [#4] Overlay draw is now unconditional — await resolved (or null) before this point.
        // No more setInterval polling. If overlay was null, we warn and save unfiltered.
        if (currentFilter === 'popup') {
            if (overlayForFilter) {
                ctx.drawImage(overlayForFilter, 0, 0, dw, dh);
            } else {
                console.warn('[capture] Popup overlay not loaded — saving without filter');
            }
            savePhoto();
        } else if (currentFilter === 'standard1') {
            if (overlayForFilter) {
                ctx.drawImage(overlayForFilter, 0, 0, dw, dh);
            } else {
                console.warn('[capture] Standard1 overlay not loaded — saving without filter');
            }
            savePhoto();
        } else {
            setTimeout(() => savePhoto(), 100);
        }
    }

    function applyActiveFilterToCanvas(ctx) {
        if (currentFilter === 'popup' && popupOverlayImg) {
            drawOverlayCover(ctx, popupOverlayImg, canvas.width, canvas.height);
        }
        if (currentFilter === 'standard1' && standard1OverlayImg) {
            drawOverlayCover(ctx, standard1OverlayImg, canvas.width, canvas.height);
        }
    }

    function drawOverlayCover(ctx, img, cw, ch) {
        const imgRatio = img.width / img.height;
        const canvasRatio = cw / ch;
        let sx = 0, sy = 0, sw = img.width, sh = img.height;
        if (imgRatio > canvasRatio) {
            sw = img.height * canvasRatio;
            sx = (img.width - sw) / 2;
        } else {
            sh = img.width / canvasRatio;
            sy = (img.height - sh) / 2;
        }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
    }

    function savePhoto() {
        console.log('[savePhoto] called, canvas:', canvas.width, 'x', canvas.height);
        try {
            const dataUrl = canvas.toDataURL('image/png');
            photos.push({ dataUrl, id: Date.now() });
            if (photos.length > MAX_PHOTOS) photos.shift();
            updateFilmstrip();
            window.lastCapturedDataUrl = dataUrl;
            preview.src = dataUrl;
            preview.style.display = 'block';
            video.style.display = 'none';
            if (liveOverlay) liveOverlay.style.display = 'none';
            setShutterMode('download');
            photoReady = true;
            try {
                if (window.collageAddCameraPhoto) {
                    window.collageAddCameraPhoto(dataUrl);
                }
            } catch (collageErr) {
                console.warn('[savePhoto] collage hook failed:', collageErr);
            }
        } catch (err) {
            console.error('[savePhoto] FATAL:', err);
        }
    }

    function downloadLastPhoto() {
        const dataUrl = window.lastCapturedDataUrl || window.activePreviewDataUrl;
        if (!dataUrl) return;
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = 'ateez-photobooth-' + Date.now() + '.png';
        document.body.appendChild(link);
        link.click();
        link.remove();
        openDownloadModal();
    }

    function openDownloadModal() {
        const modal = document.getElementById('download-modal');
        if (!modal) return;
        modal.style.display = 'flex';
    }

    function closeDownloadModal() {
        const modal = document.getElementById('download-modal');
        if (!modal) return;
        modal.style.display = 'none';
        photoReady = false;
        setShutterMode('capture');
        showLivePreview();
    }

    const modal = document.getElementById('download-modal');
    if (modal) {
        const closeModalBtn = modal.querySelector('.dm-close');
        const skipBtn = modal.querySelector('.dm-skip');
        if (closeModalBtn) closeModalBtn.addEventListener('click', (e) => { e.stopPropagation(); closeDownloadModal(); });
        if (skipBtn) skipBtn.addEventListener('click', (e) => { e.stopPropagation(); closeDownloadModal(); });
        modal.addEventListener('click', (e) => {
            if (e.target === modal || e.target.classList.contains('dm-backdrop')) {
                e.stopPropagation();
                closeDownloadModal();
            }
        });
        modal.addEventListener('click', (e) => { e.stopPropagation(); });
    }

    // ============================================
    // PAGE 3: PHOTO COLLAGE (macOS Preview style)
    // ============================================
    const PHOTOS_MANIFEST = [
        '[ATEEZ].jpg',
        'HJ.jpg',
        'JH.jpg',
        'MG.jpg',
        'SAN.jpg',
        'SH.jpg',
        'WY.jpg',
        'YH.jpg',
        'YS.jpg',
    ];
    let collageInitialized = false;

    function shuffleArray(arr) {
        const out = arr.slice();
        for (let i = out.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [out[i], out[j]] = [out[j], out[i]];
        }
        return out;
    }

    function buildPhotoWindow(src, filename, index, total) {
        const wrapper = document.createElement('div');
        wrapper.className = 'photo-window';
        wrapper.dataset.filename = filename;
        const title = filename.replace(/@2x\.\w+$/, '').replace(/\.\w+$/, '');
        wrapper.innerHTML = `
            <div class="pv-titlebar">
                <div class="pv-dots">
                    <span class="pv-dot red"></span>
                    <span class="pv-dot yellow"></span>
                    <span class="pv-dot silver"></span>
                </div>
                <span class="pv-title">${title}</span>
            </div>
            <div class="pv-body"><img src="${src}" alt="${title}"></div>
            <div class="pv-resize-handle" title="Resize">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M9 2.5L2.5 9" stroke="#aaa" stroke-width="1.4" stroke-linecap="round"/>
                    <path d="M9 5.5L5.5 9" stroke="#aaa" stroke-width="1.4" stroke-linecap="round"/>
                    <path d="M9 8.5L8.5 9" stroke="#aaa" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
            </div>
        `;
        return wrapper;
    }

    function updateTitleFontSize(win) {
        const titleEl = win ? win.querySelector('.pv-title') : null;
        const titlebar = win ? win.querySelector('.pv-titlebar') : null;
        if (!titleEl || !titlebar) return;
        const bw = titlebar.offsetWidth;
        const scaled = Math.max(7.5, Math.min(10, bw * 0.05 + 1));
        titleEl.style.fontSize = scaled + 'px';
    }

    function scatterWindow(win, canvasRect, gridPoint, totalW, totalH) {
        const img = win.querySelector('.pv-body img');
        const nw = (img && img.naturalWidth > 0) ? img.naturalWidth : 4;
        const nh = (img && img.naturalHeight > 0) ? img.naturalHeight : 3;
        const aspect = nw / nh;
        win.dataset.aspect = String(aspect);

        const isMobile = window.innerWidth < 768;
        const MAX_W = Math.min(canvasRect.width * 0.35, isMobile ? 180 : 300);
        const MIN_W = isMobile ? 100 : 180;

        const titlebar = win.querySelector('.pv-titlebar');
        const tbH = titlebar ? titlebar.offsetHeight : 22;

        const bodyW = MIN_W + Math.random() * (MAX_W - MIN_W);
        const bodyH = bodyW / aspect;
        const targetW = bodyW;
        const targetH = bodyH + tbH;

        win.style.width = `${targetW}px`;
        win.style.height = `${targetH}px`;

        void win.offsetWidth;

        const w = win.offsetWidth;
        const h = win.offsetHeight;

        const rot = (Math.random() - 0.5) * 6;
        win.style.setProperty('--random-rot', `${rot}deg`);

        const jitterX = canvasRect.width * 0.025 * (Math.random() - 0.5) * 2;
        const jitterY = canvasRect.height * 0.025 * (Math.random() - 0.5) * 2;
        const gx = gridPoint ? gridPoint.x : (15 + Math.random() * 70);
        const gy = gridPoint ? gridPoint.y : (15 + Math.random() * 70);
        let x = (gx / 100) * canvasRect.width - w / 2 + jitterX;
        let y = (gy / 100) * canvasRect.height - h / 2 + jitterY;

        x = Math.max(6, Math.min(canvasRect.width - w - 6, x));
        y = Math.max(6, Math.min(canvasRect.height - h - 6, y));

        win.style.left = `${x}px`;
        win.style.top = `${y}px`;
        updateTitleFontSize(win);
    }

    function buildGrid(N, canvasRect) {
        const cAspect = canvasRect.width / canvasRect.height;
        let cols = Math.max(2, Math.round(Math.sqrt(N) * Math.max(0.8, cAspect * 0.9)));
        let rows = Math.ceil(N / cols);
        while (cols * rows < N) rows += 1;
        const cellW = 100 / cols;
        const cellH = 100 / rows;
        const points = [];
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                points.push({
                    x: cellW * (c + 0.5),
                    y: cellH * (r + 0.5)
                });
            }
        }
        for (let i = points.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [points[i], points[j]] = [points[j], points[i]];
        }
        return points.slice(0, N);
    }

    function makeDraggable(win, canvasContainer) {
        let isDragging = false;
        let startX, startY, initialX, initialY;
        let highestZ = 100;

        function onStart(e) {
            if (e.target.closest('.pv-dots')) return;
            if (e.target.closest('.pv-resize-handle')) return;
            e.preventDefault();
            e.stopPropagation();
            isDragging = true;
            win.classList.add('dragging');
            highestZ += 1;
            win.style.zIndex = highestZ;
            const point = e.touches ? e.touches[0] : e;
            startX = point.clientX;
            startY = point.clientY;
            const rect = win.getBoundingClientRect();
            const canvasRect = canvasContainer.getBoundingClientRect();
            initialX = rect.left - canvasRect.left;
            initialY = rect.top - canvasRect.top;
        }

        function onMove(e) {
            if (!isDragging) return;
            e.preventDefault();
            const point = e.touches ? e.touches[0] : e;
            const dx = point.clientX - startX;
            const dy = point.clientY - startY;
            let newX = initialX + dx;
            let newY = initialY + dy;
            const canvasRect = canvasContainer.getBoundingClientRect();
            const maxX = Math.max(0, canvasRect.width - win.offsetWidth);
            const maxY = Math.max(0, canvasRect.height - win.offsetHeight);
            newX = Math.max(0, Math.min(newX, maxX));
            newY = Math.max(0, Math.min(newY, maxY));
            win.style.left = `${newX}px`;
            win.style.top = `${newY}px`;
        }

        function onEnd() {
            if (!isDragging) return;
            isDragging = false;
            win.classList.remove('dragging');
        }

        win.addEventListener('mousedown', onStart, { passive: false });
        win.addEventListener('touchstart', onStart, { passive: false });
        document.addEventListener('mousemove', onMove, { passive: false });
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('mouseup', onEnd);
        document.addEventListener('touchend', onEnd);
    }

    const MIN_WINDOW_W = window.innerWidth < 768 ? 85 : 150;
    const MIN_WINDOW_H = window.innerWidth < 768 ? 60 : 100;

    function makeResizable(win, canvasContainer) {
        const handle = win.querySelector('.pv-resize-handle');
        if (!handle) return;

        let resizing = false;
        let startX, startY, startW, startH;

        function onStart(e) {
            e.preventDefault();
            e.stopPropagation();
            resizing = true;
            win.classList.add('dragging');
            win.style.zIndex = (parseInt(win.style.zIndex, 10) || 1) + 1;
            const pt = e.touches ? e.touches[0] : e;
            startX = pt.clientX;
            startY = pt.clientY;
            startW = win.offsetWidth;
            startH = win.offsetHeight;
        }

        function onMove(e) {
            if (!resizing) return;
            e.preventDefault();
            const pt = e.touches ? e.touches[0] : e;
            const dx = pt.clientX - startX;
            const dy = pt.clientY - startY;

            const titlebar = win.querySelector('.pv-titlebar');
            const tbH = titlebar ? titlebar.offsetHeight : 22;
            const aspect = parseFloat(win.dataset.aspect) || (startW / Math.max(1, startH - tbH));
            const startBodyW = startW;
            const startBodyH = startH - tbH;

            const adx = Math.abs(dx);
            const ady = Math.abs(dy);
            let bodyW, bodyH;
            if (adx >= ady) {
                bodyW = startBodyW + dx;
                bodyH = bodyW / aspect;
            } else {
                bodyH = startBodyH + dy;
                bodyW = bodyH * aspect;
            }

            bodyW = Math.max(MIN_WINDOW_W, bodyW);
            bodyH = Math.max(MIN_WINDOW_H - tbH, bodyH);

            const rect = win.getBoundingClientRect();
            const canvasRect = canvasContainer.getBoundingClientRect();
            const maxW = Math.max(MIN_WINDOW_W, canvasRect.right - rect.left);
            const maxHBody = Math.max(MIN_WINDOW_H - tbH, canvasRect.bottom - rect.top - tbH);
            if (bodyW > maxW) { bodyW = maxW; bodyH = bodyW / aspect; }
            if (bodyH > maxHBody) { bodyH = maxHBody; bodyW = bodyH * aspect; }

            win.style.width = `${bodyW}px`;
            win.style.height = `${bodyH + tbH}px`;
            updateTitleFontSize(win);
        }

        function onEnd() {
            if (!resizing) return;
            resizing = false;
            win.classList.remove('dragging');
        }

        handle.addEventListener('mousedown', onStart, { passive: false });
        handle.addEventListener('touchstart', onStart, { passive: false });
        document.addEventListener('mousemove', onMove, { passive: false });
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('mouseup', onEnd);
        document.addEventListener('touchend', onEnd);
    }

    function initCollage() {
        const canvasEl = document.getElementById('collage-canvas');
        const page3 = document.getElementById('page-3');
        if (!canvasEl || !page3) return;

        if (collageInitialized) {
            requestAnimationFrame(() => {
                page3.classList.add('page-3-photos-loaded');
            });
            return;
        }

        const shuffled = shuffleArray(PHOTOS_MANIFEST);
        const total = shuffled.length;

        function doScatter() {
            return {
                width: canvasEl.offsetWidth,
                height: canvasEl.offsetHeight
            };
        }

        const gridPoints = buildGrid(total, doScatter());

        const windows = [];
        shuffled.forEach((filename, i) => {
            const src = photoUrl(filename);  // <-- CONFIG
            const win = buildPhotoWindow(src, filename, i, total);
            canvasEl.appendChild(win);
            windows.push(win);
            makeDraggable(win, canvasEl);
            makeResizable(win, canvasEl);

            const img = win.querySelector('.pv-body img');
            const finishLoad = () => {
                // [#7] was: scatterWindow(win, doScatter(), gridPoints[i], doScatter().width, doScatter().height);
                //       doScatter() called 3x = 3 offsetWidth reads = 3 reflows per photo.
                //       Destructure once, reuse. 3N reflows → N reflows.
                const dims = doScatter();
                scatterWindow(win, dims, gridPoints[i], dims.width, dims.height);
                const loaded = canvasEl.querySelectorAll('.photo-window').length;
                if (loaded === total) {
                    collageInitialized = true;
                    requestAnimationFrame(() => {
                        page3.classList.add('page-3-photos-loaded');
                    });
                }
            };
            img.onload = finishLoad;
            img.onerror = () => {
                console.warn(`Collage photo missing: ${src}`);
                win.remove();
            };
            if (img.complete && img.naturalWidth > 0) {
                requestAnimationFrame(finishLoad);
            }
        });

        requestAnimationFrame(() => {
            setTimeout(() => {
                if (canvasEl.querySelectorAll('.photo-window').length > 0) {
                    collageInitialized = true;
                    page3.classList.add('page-3-photos-loaded');
                }
            }, 200);
        });

        window.collageAddCameraPhoto = function(dataUrl) {
            const existingCount = canvasEl.querySelectorAll('.photo-window').length;
            const win = buildPhotoWindow(dataUrl, 'camera-shot-' + Date.now(), existingCount, existingCount + 1);
            const titleEl = win.querySelector('.pv-title');
            if (titleEl) titleEl.textContent = 'My Shot';
            canvasEl.appendChild(win);
            makeDraggable(win, canvasEl);
            makeResizable(win, canvasEl);

            const img = win.querySelector('.pv-body img');
            const onReady = () => {
                const canvasRect = canvasEl.getBoundingClientRect();
                scatterWindow(win, canvasRect);
            };
            img.onload = onReady;
            img.onerror = onReady;
            if (img.complete && img.naturalWidth > 0) {
                requestAnimationFrame(onReady);
            }
        };
    };
});